import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { formatFileSize, checkFileSize } from '../../utils/fileUtils';

const { FiUpload, FiX, FiAlertTriangle, FiCheck } = FiIcons;

const BatchFileUpload = ({
  onFilesUpload,
  acceptedTypes,
  uploadText,
  supportText,
  fileType = 'image',
  maxFiles = 10
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [validationResults, setValidationResults] = useState([]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const validateFiles = (files) => {
    const results = [];
    const validFiles = [];

    for (let i = 0; i < Math.min(files.length, maxFiles); i++) {
      const file = files[i];
      const sizeCheck = checkFileSize(file, fileType);

      results.push({
        file,
        ...sizeCheck
      });

      if (sizeCheck.isValid) {
        validFiles.push(file);
      }
    }

    return { results, validFiles };
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const processFiles = (files) => {
    if (files.length === 0) return;

    if (files.length > maxFiles) {
      if (window.showToast) {
        window.showToast(`${t('maxFiles')} ${maxFiles} ${t('files')}`, 'warning');
      }
    }

    const { results, validFiles } = validateFiles(files);
    setSelectedFiles(validFiles);
    setValidationResults(results);

    const errors = results.filter(r => !r.isValid);
    const warnings = results.filter(r => r.isValid && r.level === 'warning');

    if (errors.length > 0) {
      if (window.showToast) {
        window.showToast(`${errors.length} ${t('fileTooLarge')}`, 'error');
      }
    }

    if (warnings.length > 0) {
      if (window.showToast) {
        window.showToast(`${warnings.length} ${t('fileWarning')}`, 'warning');
      }
    }

    if (validFiles.length > 0) {
      if (window.showToast) {
        window.showToast(`${t('filesSelected')} ${validFiles.length} ${t('files')}`, 'success');
      }
    }
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newResults = validationResults.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setValidationResults(newResults);
  };

  const handleConfirm = () => {
    if (selectedFiles.length === 0) {
      if (window.showToast) {
        window.showToast(t('pleaseSelectFiles'), 'warning');
      }
      return;
    }

    onFilesUpload(selectedFiles);
  };

  const handleClear = () => {
    setSelectedFiles([]);
    setValidationResults([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 獲取最大文件大小
  const getMaxSize = () => {
    return fileType === 'pdf' ? '100MB' : '50MB';
  };

  return (
    <div className="space-y-4">
      {/* 上傳區域 */}
      <motion.div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-400 transition-colors"
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex flex-col items-center gap-4">
          <motion.div
            className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <SafeIcon icon={FiUpload} className="w-8 h-8 text-purple-600" />
          </motion.div>

          <div>
            <h4 className="text-lg font-medium text-gray-800 mb-2">
              {uploadText}
            </h4>
            <p className="text-sm text-gray-500">
              {supportText}
            </p>

            <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <SafeIcon icon={FiAlertTriangle} className="w-3 h-3" />
                <span>
                  {t('maxFileSize')} {getMaxSize()}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span>{t('maxFiles')} {maxFiles} {t('files')}</span>
              </div>
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileChange}
          className="hidden"
          multiple
        />
      </motion.div>

      {/* 文件列表 */}
      <AnimatePresence>
        {selectedFiles.length > 0 && (
          <motion.div
            className="bg-gray-50 rounded-lg p-4 space-y-3"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-gray-800">
                {t('filesSelected')} {selectedFiles.length} {t('files')}
              </h4>
              <button
                onClick={handleClear}
                className="text-sm text-gray-600 hover:text-red-600 transition-colors"
              >
                {t('clearAll')}
              </button>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {validationResults.map((result, index) => (
                <motion.div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    result.isValid
                      ? 'bg-white border-gray-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        result.isValid
                          ? result.level === 'warning'
                            ? 'bg-orange-100'
                            : 'bg-green-100'
                          : 'bg-red-100'
                      }`}
                    >
                      <SafeIcon
                        icon={result.isValid ? FiCheck : FiX}
                        className={`w-4 h-4 ${
                          result.isValid
                            ? result.level === 'warning'
                              ? 'text-orange-600'
                              : 'text-green-600'
                            : 'text-red-600'
                        }`}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {result.file.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {formatFileSize(result.size)}
                        </span>
                        {result.level === 'warning' && (
                          <span className="text-xs text-orange-600">
                            ⚠️ {t('fileWarning')}
                          </span>
                        )}
                        {!result.isValid && (
                          <span className="text-xs text-red-600">
                            ❌ {result.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {result.isValid && (
                    <button
                      onClick={() => removeFile(index)}
                      className="ml-2 p-2 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4 text-gray-600" />
                    </button>
                  )}
                </motion.div>
              ))}
            </div>

            {/* 確認按鈕 */}
            <motion.button
              className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              onClick={handleConfirm}
              disabled={selectedFiles.length === 0}
              whileHover={{ scale: selectedFiles.length === 0 ? 1 : 1.02 }}
              whileTap={{ scale: selectedFiles.length === 0 ? 1 : 0.98 }}
            >
              {t('startProcessing')} ({selectedFiles.length} {t('files')})
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BatchFileUpload;